import { TreeItemType } from '@features/campaigns/builder/containers/FormPages/FormPages.types.ts';
import { GeoPayloadCollectionModes, GeoTargetingConfig } from '@features/campaigns/shared/config/geoTargetingConfig.ts';

export interface GeoPayloadIds {
  citiesIds: string[];
  provincesIds: string[];
}

export interface GeoPayloadTreeNode {
  id: string;
  type?: TreeItemType;
  parentProvinceId?: string;
  parentProvinceCityCount?: number;
  children?: GeoPayloadTreeNode[];
}

function collectGeoPayloadIdsFromSelectedNodesByType(treeNodes: GeoPayloadTreeNode[]): GeoPayloadIds {
  const citiesIds: string[] = [];
  const provincesIds: string[] = [];

  treeNodes.forEach((treeNode) => {
    if (treeNode.type === TreeItemType.City) {
      citiesIds.push(treeNode.id);
    } else if (treeNode.type === TreeItemType.Province) {
      provincesIds.push(treeNode.id);
    }
  });

  return {
    citiesIds,
    provincesIds,
  };
}

function collectCityNodesWithParentProvinces(treeNodes: GeoPayloadTreeNode[]): GeoPayloadTreeNode[] {
  const cityNodes: GeoPayloadTreeNode[] = [];

  const visitNode = (treeNode: GeoPayloadTreeNode) => {
    if (treeNode.type === TreeItemType.City) {
      cityNodes.push(treeNode);
      return;
    }

    treeNode.children?.forEach(visitNode);
  };

  treeNodes.forEach(visitNode);

  return cityNodes;
}

function collectSelectedLeafProvinceIds(treeNodes: GeoPayloadTreeNode[]): string[] {
  const leafProvinceIds: string[] = [];

  const visitNode = (treeNode: GeoPayloadTreeNode) => {
    if (treeNode.type === TreeItemType.Province && (treeNode.children?.length ?? 0) === 0) {
      leafProvinceIds.push(treeNode.id);
      return;
    }

    if (treeNode.type === TreeItemType.City) {
      return;
    }

    treeNode.children?.forEach(visitNode);
  };

  treeNodes.forEach(visitNode);

  return leafProvinceIds;
}

function collectFullySelectedProvinceIds(cityNodes: GeoPayloadTreeNode[]): string[] {
  const selectedCityIdsByProvinceId = new Map<string, Set<string>>();
  const expectedCityCountByProvinceId = new Map<string, number>();

  cityNodes.forEach((cityNode) => {
    if (!cityNode.parentProvinceId) {
      return;
    }

    const selectedCityIds = selectedCityIdsByProvinceId.get(cityNode.parentProvinceId) ?? new Set<string>();
    selectedCityIds.add(cityNode.id);
    selectedCityIdsByProvinceId.set(cityNode.parentProvinceId, selectedCityIds);

    if (cityNode.parentProvinceCityCount !== undefined) {
      expectedCityCountByProvinceId.set(cityNode.parentProvinceId, cityNode.parentProvinceCityCount);
    }
  });

  const fullySelectedProvinceIds: string[] = [];

  selectedCityIdsByProvinceId.forEach((selectedCityIds, provinceId) => {
    const expectedCityCount = expectedCityCountByProvinceId.get(provinceId);

    if (expectedCityCount !== undefined && expectedCityCount > 0 && selectedCityIds.size === expectedCityCount) {
      fullySelectedProvinceIds.push(provinceId);
    }
  });

  return fullySelectedProvinceIds;
}

function collectGeoPayloadIdsFromNestedCitiesWithParentProvinces(treeNodes: GeoPayloadTreeNode[]): GeoPayloadIds {
  const cityNodes = collectCityNodesWithParentProvinces(treeNodes);
  const citiesIds = cityNodes.map((cityNode) => cityNode.id);
  const provincesIds = [
    ...new Set([...collectFullySelectedProvinceIds(cityNodes), ...collectSelectedLeafProvinceIds(treeNodes)]),
  ];

  return {
    citiesIds,
    provincesIds,
  };
}

const GEO_PAYLOAD_COLLECTORS: Record<
  GeoTargetingConfig['geoPayloadCollectionMode'],
  (treeNodes: GeoPayloadTreeNode[]) => GeoPayloadIds
> = {
  [GeoPayloadCollectionModes.SELECTED_NODES_BY_TYPE]: collectGeoPayloadIdsFromSelectedNodesByType,
  [GeoPayloadCollectionModes.NESTED_CITIES_WITH_PARENT_PROVINCES]:
    collectGeoPayloadIdsFromNestedCitiesWithParentProvinces,
};

export function collectGeoPayloadIds(
  treeNodes: GeoPayloadTreeNode[] | null,
  config: GeoTargetingConfig,
): GeoPayloadIds {
  if (!treeNodes || treeNodes.length === 0) {
    return {
      citiesIds: [],
      provincesIds: [],
    };
  }

  return GEO_PAYLOAD_COLLECTORS[config.geoPayloadCollectionMode](treeNodes);
}
